import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Direct Upload
import { DirectUpload } from '@rails/activestorage';

// MUI
import Typography from "@mui/material/Typography";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

// MUI ICONS
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MenuIcon from '@mui/icons-material/Menu';

// Draggable
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Icons
import { LuCloudUpload } from "react-icons/lu";

// Components
import FileRow from "../../../../../../../components/AttachMaterials/FileRow";
import FilePreviewBadge from "../../../../../../../components/FilePreviewBadge";

// Context
import { useCoursesNewContext } from "../../../../context";

// Styles
import styles from "./style.module.scss";

// Utils
import * as urls from "../../../../../../../utils/urls";

// Types
import { UploadFile } from "../../../../../../../types";

// Hooks
import useCsrfToken from "../../../../../../../utils/hooks/csfrToken";
import OverviewModal from "./OverviewModal";


interface AddEditRowProps {
  index: number;
  unitId: string | number;
  data: {
    id: string | number;
    title: string;
    description: string;
    files: UploadFile[];
  };
  onSave: (success: boolean) => void;
  onDelete: (success: boolean) => void;
}

const AddEditRow: React.FC<AddEditRowProps> = ({ unitId, index, data, onSave, onDelete }) => {
  const { t } = useTranslation();
  const csrfToken = useCsrfToken();

  // Drag and Drop
  const {
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition
  } = useSortable({
    id: `sub_unit_${data.id.toString()}`,
    data: {
      type: 'sub_unit',
      id: data.id.toString()
    }
  });

  // Context
  const { course } = useCoursesNewContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [openOverview, setOpenOverview] = useState<boolean>(false);

  // Form
  const [order, setOrder] = useState<number>(index);
  const [title, setTitle] = useState<string>('');
  const [error, setError] = useState<{ title: boolean }>({ title: false });
  const [files, setFiles] = useState<string[]>([]);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  const CourseID = useMemo(() => {
    return course?.id || 'UNKNOWN_COURSE_ID';
  }, [course]);

  // Methods
  const handleDelete = useCallback(async () => {
    if (!data.id) {
      onDelete(true);
      return;
    }

    setLoading(true);
    try {
      const options = {
        method: 'DELETE',
        headers: {
          'X-CSRF-TOKEN': csrfToken,
        },
      };

      const response = await fetch(urls.subUnitUrl(CourseID, unitId, data.id), options);
      onDelete(response.ok);
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [csrfToken, CourseID, unitId, data.id]);

  const handleSave = useCallback(async () => {
    if (title.trim() === '') {
      setError({ title: true });
      return;
    }

    setLoading(true);
    try {
      const url: string = isAdding ? urls.subUnitsUrl(CourseID, unitId) : urls.subUnitUrl(CourseID, unitId, data.id);
      const formData = new FormData();
      formData.append('sub_unit[title]', title);
      formData.append('sub_unit[description]', '');
      files.forEach((file) => {
        formData.append('sub_unit[files][]', file);
      });

      const options = {
        method: isAdding ? 'POST' : 'PATCH',
        headers: {
          'X-CSRF-Token': csrfToken
        },
        body: formData
      };

      const response = await fetch(url, options);

      onSave(response.ok);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [csrfToken, isAdding, unitId, CourseID, data, title, files]);

  const uploadFiles = useCallback(async (files: File[]) => {
    const uploadSingleFile = (file: File) => {
      return new Promise<string>((resolve, reject) => {
        const directUpload = new DirectUpload(file, urls.directUploadUrl());
        directUpload.create((error: any, attributes: any) => {
          if (error) {
            console.error(error);
            reject(error);
          } else {
            resolve(attributes.signed_id);
          }
        });
      });
    };

    try {
      const signedIds = await Promise.all(files.map(uploadSingleFile));
      setFiles(prev => [...prev, ...signedIds]);

      // Save the changes to the database
      handleSave();
    } catch (error) {
      // Optionally handle error
    }
  }, []);

  const onChangeOrder = useCallback(async () => {
    setOrder(index);

    // If the index has changed and the sub unit has an id, update the order in the database
    if (index !== order && data.id) {
      setLoading(true);

      try {
        const url = urls.subUnitUrl(CourseID, unitId, data.id);
        const formData = new FormData();
        formData.append('sub_unit[order]', index.toString());

        const options = {
          method: 'PATCH',
          headers: { 'X-CSRF-Token': csrfToken },
          body: formData
        };

        const response = await fetch(url, options);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  }, [index, order]);

  useEffect(() => {
    if (data) {
      setIsAdding(data.id ? false : true);
      setTitle(data.title);
    }
  }, [data]);

  useEffect(() => {
    // If files are uploaded and the sub unit is not being added, save the sub unit
    if (files && !isAdding) handleSave();
  }, [files, isAdding]);

  useEffect(() => {
    onChangeOrder();
  }, [index]);

  // Handlers
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) uploadFiles(Array.from(files));
  }, []);

  const handleOverview = useCallback(() => {
    setOpenOverview(true);
  }, []);

  const handleDeleteFile = useCallback((signedId: string) => {
    setFiles(prev => prev.filter(file => file !== signedId));

    // Save the changes to the database
    handleSave();
  }, []);

  return (
    <div
      className={styles.root}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: transition ? transition.toString() : undefined,
      }}
    >
      <IconButton
        color='secondary'
        ref={setActivatorNodeRef}
        className={styles.dragHandle}
        disabled={loading || isAdding}
        {...listeners}
      >
        <MenuIcon />
      </IconButton>

      <div className={styles.header}>
        <FormLabel>
          {t('courses.new.structure.sub_unit.label', { index })}
        </FormLabel>

        <div className={styles.header_right}>
          <Button
            color='primary'
            variant='text'
            classes={{
              root: styles.overviewButton
            }}
            disabled={(isAdding && !title) || loading}
            onClick={isAdding ? handleSave : handleOverview}
          >
            {isAdding ? t('actions.save') : t('courses.new.structure.sub_unit.overview')}
          </Button>

          <IconButton
            color='primary'
            classes={{
              root: styles.deleteButton
            }}
            disabled={loading}
            onClick={() => fileInputRef.current?.click()}
          >
            <LuCloudUpload />
          </IconButton>

          <IconButton
            color='error'
            classes={{
              root: styles.deleteButton
            }}
            disabled={loading}
            onClick={handleDelete}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
      </div>

      <div className={styles.content}>
        {isAdding
          ? (
            <TextField
              variant='standard'
              slotProps={{
                input: {
                  sx: { border: 'none', outline: 'none', boxShadow: 'none' }
                }
              }}
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={error.title}
              helperText={error.title && t('courses.new.structure.sub_unit.title_error')}
            />
          )
          : (
            <Typography variant='body1' color='textPrimary' className={styles.title}>
              {title}
            </Typography>
          )
        }
      </div>

      <div className={styles.files}>
        {files.map((file: string) => (
          <FilePreviewBadge
            key={file}
            fileId={file}
            onRemove={handleDeleteFile}
          />
        ))}
      </div>

      <input
        type="file"
        multiple
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="application/pdf"
        onChange={handleFileUpload}
      />

      <OverviewModal
        open={openOverview}
        onClose={() => setOpenOverview(false)}
      />
    </div>
  )
}

export default AddEditRow;
